'use client';

import { useEffect, useState } from 'react';
import { enablePush } from '@/lib/push/enablePush';

/**
 * Real-time picks opt-in (product-play Move 03). Rendered by the radar ONLY for subscribers, and ONLY
 * when REALTIME_PICKS_ENABLED (the server gates both). Flipping it on sets users.preferences.realtimePicks
 * and best-effort enables web push on this device, so a newly-armed pick reaches their phone.
 */
export default function RealtimePicksToggle() {
  const [enabled, setEnabled] = useState<boolean | null>(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState('');

  useEffect(() => {
    let live = true;
    fetch('/api/user/realtime-picks')
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (live && d && d.available && d.eligible) setEnabled(!!d.enabled); })
      .catch(() => {});
    return () => { live = false; };
  }, []);

  // Not eligible / still loading / tier dark → render nothing.
  if (enabled === null) return null;

  async function toggle() {
    if (busy) return;
    setBusy(true);
    setNote('');
    const next = !enabled;
    try {
      if (next) {
        // Best-effort: register the SW + request permission + subscribe, so the push can actually land.
        try { await enablePush(); } catch { /* the preference still saves; they just won't get device push yet */ }
      }
      const r = await fetch('/api/user/realtime-picks', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ enabled: next }),
      });
      if (r.ok) {
        setEnabled(next);
        if (next && typeof Notification !== 'undefined' && Notification.permission !== 'granted') {
          setNote('Saved. Allow notifications on this device to get them as a push.');
        } else if (next) {
          setNote('On. New flags will reach you the moment they fire.');
        }
      } else {
        setNote('Could not save, please try again.');
      }
    } catch {
      setNote('Could not save, please try again.');
    }
    setBusy(false);
  }

  return (
    <div className="mt-6 flex items-center gap-3 rounded-2xl border border-emerald-500/25 bg-emerald-500/[0.06] px-4 py-3">
      <div className="min-w-0 flex-1">
        <p className="text-sm font-bold text-emerald-200">Get new flags the moment they fire</p>
        <p className="mt-0.5 text-xs text-emerald-200/60">
          {note || 'Real-time push to your phone the instant the Watcher flags a name. Subscriber benefit.'}
        </p>
      </div>
      <button
        type="button"
        onClick={toggle}
        disabled={busy}
        aria-pressed={enabled}
        className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${enabled ? 'bg-emerald-500' : 'bg-zinc-700'} ${busy ? 'opacity-60' : ''}`}
      >
        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${enabled ? 'translate-x-6' : 'translate-x-1'}`} />
      </button>
    </div>
  );
}
